import { ColumnConfig } from "../layout/table/columnConfig";
import * as React from "react";
import Table from "../layout/table/Table";
import { ValidationConfigurationRow } from "./ValidationConfigurationRow";
import { Dispatch } from "react";
import { sortByProp } from "../language/module";
import { getResource } from "../language/resources/getResource";
import {
  getChangeHistoryForEffectiveDateRangeRule,
  effectiveDateRangeRuleUniqueKey,
} from "./module";
import { IAppState } from "../core/redux/IAppState";
import { useSelector } from "react-redux";
import {
  EditableEffectiveDateRangeAccessRule,
  LiftAccessRuleHistory,
} from "./models";
import { SerializedStyles } from "@emotion/react";
import { useEffectiveDateRangeValidation } from "./useEffectiveDateRangeValidation";

type Props = {
  data: EditableEffectiveDateRangeAccessRule[];
  setConfigurations: Dispatch<
    React.SetStateAction<EditableEffectiveDateRangeAccessRule[]>
  >;
  locked: boolean;
  isLoadingConfigurations: boolean;
  isLoadingHistory: boolean;
  showChangeHistory: boolean;
  changeHistoryData: LiftAccessRuleHistory[];
  rootCss?: SerializedStyles;
};

export const ValidationConfigurationTable: React.FC<Props> = (props) => {
  const resources = useSelector((state: IAppState) => state.locale);

  // Compute validation results for the current data to pass down to rows for error/warning display and history association
  const validation = useEffectiveDateRangeValidation(props.data);

  const columnConfig: Array<ColumnConfig<EditableEffectiveDateRangeAccessRule>> =
    [
      {
        id: 1,
        tooltipText: getResource(
          resources,
          "VALIDATION_CONFIG_TABLE_PRODUCT",
        ),
        title: getResource(resources, "VALIDATION_CONFIG_TABLE_PRODUCT"),
        onSort: sortByProp("product", true),
        defaultSortingColumn: true,
        defaultSortingDirection: "asc",
      },
      {
        id: 2,
        tooltipText: getResource(
          resources,
          "VALIDATION_CONFIG_TABLE_SEASON",
        ),
        title: getResource(resources, "VALIDATION_CONFIG_TABLE_SEASON"),
        onSort: sortByProp("season", true),
      },
      {
        id: 3,
        tooltipText: getResource(
          resources,
          "VALIDATION_CONFIG_TABLE_FULFILLMENT_SEASON",
        ),
        title: getResource(
          resources,
          "VALIDATION_CONFIG_TABLE_FULFILLMENT_SEASON",
        ),
        onSort: sortByProp("fulfillmentSeason", true),
      },
      {
        id: 4,
        tooltipText: getResource(
          resources,
          "VALIDATION_CONFIG_TABLE_START_DATE",
        ),
        title: getResource(
          resources,
          "VALIDATION_CONFIG_TABLE_START_DATE",
        ),
      },
      {
        id: 5,
        tooltipText: getResource(
          resources,
          "VALIDATION_CONFIG_TABLE_EXPIRE_DATE",
        ),
        title: getResource(
          resources,
          "VALIDATION_CONFIG_TABLE_EXPIRE_DATE",
        ),
      },
    ];

  const renderRow = (
    accessRule: EditableEffectiveDateRangeAccessRule,
  ): React.ReactNode => (
    <ValidationConfigurationRow
      key={accessRule.liftAccessRuleId ? accessRule.liftAccessRuleId : effectiveDateRangeRuleUniqueKey(accessRule)}
      entitlement={accessRule}
      locked={props.locked}
      setConfigurations={props.setConfigurations}
      validation={validation}
      showChangeHistory={props.showChangeHistory}
      effectiveDateRangeHistory={getChangeHistoryForEffectiveDateRangeRule(
        accessRule,
        props.changeHistoryData,
      )}
    />
  );

  return (
    <Table
      key="validationConfigurationTable"
      noDataMessage={getResource(
        resources,
        "VALIDATION_CONFIG_NO_DATA",
      )}
      isLoading={
        props.isLoadingConfigurations || props.isLoadingHistory
      }
      columns={columnConfig}
      data={props.data}
      renderRow={renderRow}
      rootCss={props.rootCss}
    />
  );
};
